import { useLocation } from "react-router-dom";
import {
	useLazyGetExportMaintenanceExcelQuery,
	useLazyGetExportStopQuery,
	useLazyGetExportSummaryQuery,
	useLazyGetExportVehicleRoutesQuery,
	useLazyGetExportVehicleTripsQuery,
} from "../Redux/service/NewReports/ExportReport";
import {
	useLazyGetAttendanceReportFileQuery,
	useLazyGetDailyAttendanceReportFileQuery,
	useLazyGetDurationFileQuery,
	useLazyGetProductivityFileQuery,
} from "../Redux/service/NewReports/Workforce";
import { useLazyGeServiceRequestFileQuery } from "../Redux/service/NewReports/ServicesRequestReports";
import { useLazyGetShelfAvailabilityExportQuery } from "../Redux/service/NewReports/ShelfAvailability";
import { useLazyGetShelfMovementReportExcelQuery } from "../Redux/service/NewReports/ShelfMovement";

export const useExportHandlers = () => {
	const [exportRoutes] = useLazyGetExportVehicleRoutesQuery();
	const [exportTrips] = useLazyGetExportVehicleTripsQuery();
	const [exportStop] = useLazyGetExportStopQuery();
	const [exportSummary] = useLazyGetExportSummaryQuery();
	const [exportDurationEfficency] = useLazyGetDurationFileQuery();
	const [exportProductivity] = useLazyGetProductivityFileQuery();
	const [exportRequests] = useLazyGeServiceRequestFileQuery();
	const [exportDailyAttendance] = useLazyGetDailyAttendanceReportFileQuery();
	const [exportAttendance] = useLazyGetAttendanceReportFileQuery();
	const [exportShelfAvailability] = useLazyGetShelfAvailabilityExportQuery();
	const [exportMaintenanceExcel] = useLazyGetExportMaintenanceExcelQuery();
	const [exportShelfMovement] = useLazyGetShelfMovementReportExcelQuery();
	const { pathname } = useLocation();
	const shorturl = pathname.replace(/\/+$/, "").split("/").pop();
	console.log("shorturl", shorturl);
	async function getReportBlob({
		shorturl,
		startDate,
		endDate,
		format = "excel",
	}) {
		if (shorturl === "efficiency") {
			return await exportDurationEfficency({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else if (shorturl === "productivity") {
			return await exportProductivity({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else if (shorturl === "requests") {
			return await exportRequests({ val: format, startDate, endDate }).unwrap();
		} else if (shorturl === "dailyAttendance") {
			return await exportDailyAttendance({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else if (shorturl === "attendance") {
			return await exportAttendance({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else {
			throw new Error(`Unknown report type: ${shorturl}`);
		}
	}
	function getReportFilename(shorturl, format = "xlsx") {
		if (shorturl === "efficiency") {
			return `durationEfficiency.${format}`;
		} else if (shorturl === "productivity") {
			return `productivity.${format}`;
		} else if (shorturl === "requests") {
			return `requests.${format}`;
		} else if (shorturl === "dailyAttendance") {
			return `Attendance_Report.${format}`;
		} else if (shorturl === "attendance") {
			return `Shift_Comp.${format}`;
		} else {
			return `report.${format}`; // fallback
		}
	}
	const buildTimestamp = () =>
		new Date()
			.toISOString()
			.replace(/[:.]/g, "-")
			.replace("T", "_")
			.split("Z")[0];
	function formatDate(dateString) {
		return decodeURIComponent(dateString).split("T")[0];
	}
	const handleExportReport = async ({
		fileName,
		exportIds,
		startDate,
		endDate,
		filters: maintenanceFilterQuery = "",
	}) => {
		if (shorturl === "maintenance") {
			if (!fileName) {
				console.warn("Missing export parameters");
				return;
			}
			try {
				const blob = await exportMaintenanceExcel({
					filterQuery: maintenanceFilterQuery,
				}).unwrap();
				const url = window.URL.createObjectURL(new Blob([blob]));
				const link = document.createElement("a");
				link.href = url;
				const timestamp = new Date()
					.toISOString()
					.replace(/[:.]/g, "-")
					.replace("T", "_")
					.split("Z")[0];
				link.setAttribute("download", `Maintenance_Report_${timestamp}.xlsx`);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			} catch (err) {
				console.error("Export failed", err);
			}
			return;
		}

		if (
			shorturl !== "efficiency" &&
			shorturl !== "productivity" &&
			shorturl !== "requests" &&
			shorturl !== "dailyAttendance" &&
			shorturl !== "attendance" &&
			shorturl !== "shelfAvailability" &&
			shorturl !== "shelfMovement"
		) {
			if (!fileName || !exportIds || !startDate || !endDate) {
				console.warn("Missing export parameters");
				return;
			}
		}

		const params = {
			from: startDate,
			to: endDate,
			selectedids: exportIds,
		};

		try {
			if (shorturl === "shelfAvailability") {
				const blob = await exportShelfAvailability({
					formId: process.env.REACT_APP_SHELF_FORM_ID,
				}).unwrap();
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", "ShelfAvailability.xlsx");
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
				return;
			}
			if (shorturl === "shelfMovement") {
				if (!startDate || !endDate) {
					console.warn("Missing export parameters: startDate/endDate required");
					return;
				}
				try {
					const blob = await exportShelfMovement({
						format: "excel",
						fromDate: formatDate(startDate),
						toDate: formatDate(endDate),
					}).unwrap();
					const url = window.URL.createObjectURL(new Blob([blob]));
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute(
						"download",
						`ShelfMovement_${buildTimestamp()}.xlsx`,
					);
					document.body.appendChild(link);
					link.click();
					link.remove();
					window.URL.revokeObjectURL(url);
				} catch (err) {
					console.error("Export failed", err);
				}
				return;
			}

			if (
				shorturl !== "efficiency" &&
				shorturl !== "productivity" &&
				shorturl !== "requests" &&
				shorturl !== "dailyAttendance" &&
				shorturl !== "attendance"
			) {
				switch (fileName) {
					case "Routes Report": {
						const blob = await exportRoutes(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", "Routes_Report.xlsx");
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					case "Trips Report": {
						const blob = await exportTrips(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", "Trips_Report.xlsx");
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					case "Stop Report": {
						const blob = await exportStop(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", "Stop_Report.xlsx");
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					case "Summary Report": {
						const blob = await exportSummary(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const timestamp = new Date()
							.toISOString()
							.replace(/[:.]/g, "-")
							.replace("T", "_")
							.split("Z")[0];
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", `Report ${timestamp}.xlsx`);
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					default:
						console.warn(`No export logic defined for file: ${fileName}`);
						break;
				}
			} else {
				const blob = await getReportBlob({
					shorturl,
					startDate,
					endDate,
					format: "excel",
				});
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", getReportFilename(shorturl, "xlsx"));

				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			}
		} catch (err) {
			console.error("Export failed", err);
		}
	};

	return { handleExportReport };
};
